import { useDisclosure } from '@ds';
import { toast } from 'react-toastify';
import { CaseStatus, ClinicalData, ClinicalItemType, OrganType, Randomization } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { getClinicalRecordByType, isRandomizationScs } from '@/utils/helpers';
import { useTransferReallocationData } from '@/queries/orchestrator';
import { retrieveUnifiedError } from '@/api/utils';

type useProceedProps = {
  nextStatus: CaseStatus;
  caseId: Uuid;
  updateStatus: (caseId: string, nextStatus: CaseStatus) => Promise<void>;
  clinicalData: ClinicalData;
  randomization?: Randomization;
  reAllocationCaseId: Uuid | null | undefined;
  organType: OrganType;
};

export const useProceedToNextCaseStatus = ({
  nextStatus,
  caseId,
  updateStatus,
  clinicalData,
  randomization,
  reAllocationCaseId,
  organType,
}: useProceedProps) => {
  const onOcs = useDisclosure();
  const offOcs = useDisclosure();
  const { mutateAsync: transferReallocationData, isPending: isReallocationDataTransferPending } =
    useTransferReallocationData();

  const proceedWithReallocationDataTransfer = async (fromCaseId: Uuid) => {
    try {
      await transferReallocationData({ fromCaseId, toCaseId: caseId });
      toast.success('Status successfully updated, data transferred from reallocation case');
    } catch (error) {
      const e = retrieveUnifiedError(error);
      toast.error(e.message);
    }
  };

  const onProceedStatusClick = async (proceedTo?: CaseStatus) => {
    const targetStatus = proceedTo ?? nextStatus;
    const isKidney = organType === OrganType.Kidney;
    const isScs = isRandomizationScs(randomization);
    const onDeviceClinicalType = isScs ? ClinicalItemType.OrganOnSCS : ClinicalItemType.OrganOnOCS;
    const offDeviceClinicalType = isScs ? ClinicalItemType.OffSCS : ClinicalItemType.OffOCS;
    const offDeviceClinicalTypeLungs = isScs
      ? ClinicalItemType.ScsTerminationOption
      : ClinicalItemType.PerfusionTerminationOption;

    const organWasOnDevice = getClinicalRecordByType(clinicalData, onDeviceClinicalType);
    const offDeviceRecord = getClinicalRecordByType(clinicalData, offDeviceClinicalType);
    const lungsOffDeviceRecord = getClinicalRecordByType(clinicalData, offDeviceClinicalTypeLungs);
    const organWasOffDevice = offDeviceRecord || lungsOffDeviceRecord;

    if (targetStatus === CaseStatus.Accepted && reAllocationCaseId) {
      proceedWithReallocationDataTransfer(reAllocationCaseId);
    } else if (targetStatus === CaseStatus.OrganOnOCS && !organWasOnDevice && !isKidney) {
      onOcs.onOpen();
    } else if (targetStatus === CaseStatus.OrganOffOCS && !organWasOffDevice && !isKidney) {
      offOcs.onOpen();
    } else {
      updateStatus(caseId, targetStatus);
    }
  };

  return {
    onOcs,
    offOcs,
    onProceedStatusClick,
    isReallocationDataTransferPending,
  };
};
